import { didInstalledMobileApp } from '@allurion/domain';
import { enhanceError } from '@allurion/utils';
import { useEffect, useRef, useCallback } from 'react';

import { useTrackEvent } from 'src/analytics/analytics';
import { PatientProfile } from 'src/domain/patient/Patients';
import { useCurrentProvider } from 'src/hooks/useCurrentProvider';
import { Logger } from 'src/services/Logger';

import TwilioConversationsService from './TwilioConversationsService';

export function useSetupTwilioConversation(patient?: PatientProfile) {
  const { provider } = useCurrentProvider();
  const { trackTwilioChatFailure } = useTrackEvent();
  // Tracks the patient we've already started setup for. Using the patient
  // identity (not a plain boolean) means a completed/failed run can't clear the
  // guard for a *different* patient, and effect churn can't fire a duplicate
  // setup for the same patient. Switching patients naturally re-enables setup.
  const setupStartedForRef = useRef<string | undefined>(undefined);
  const identity = provider.CognitoUserName;

  const patientId = patient?.uid;
  const patientUsername = patient?.userid;
  const providerName = `${provider?.FirstName} ${provider?.LastName}`;
  const isPatientAvailable = patient && didInstalledMobileApp(patient);

  const setupConversation = useCallback(async () => {
    if (!patientUsername || !identity || !patientId) {
      return;
    }

    // Claim the guard only once all prerequisites are satisfied (above), so an
    // early-return while identity/patientId aren't ready yet leaves the ref
    // unset and setup retries for this patient once they arrive. If the effect
    // set the ref before these guards, a not-ready run would permanently block
    // the retry.
    setupStartedForRef.current = patientUsername;

    try {
      await TwilioConversationsService.setupConversation({
        identity,
        patientIdentity: patientUsername,
        providerName,
        patientId: patientId.toString(),
      });
    } catch (error) {
      Logger.captureException(enhanceError(error, { prefix: 'useSetupTwilioConversation' }));
      trackTwilioChatFailure({ error });
      // Allow a retry for *this* patient after a failure — but only if we haven't
      // already moved on to another patient, so we never clobber a newer run's guard.
      if (setupStartedForRef.current === patientUsername) {
        setupStartedForRef.current = undefined;
      }
    }
  }, [identity, patientId, patientUsername, providerName, trackTwilioChatFailure]);

  useEffect(() => {
    if (!isPatientAvailable) {
      return;
    }

    // Already started (or finished) setup for this patient — don't fire again on
    // unrelated re-renders. A patient switch changes patientUsername, so the guard
    // no longer matches and setup runs for the new patient. The ref is claimed
    // inside setupConversation() (after its prerequisite guards) so a not-ready
    // run doesn't wrongly block a later retry.
    if (setupStartedForRef.current === patientUsername) {
      return;
    }

    setupConversation();
  }, [isPatientAvailable, patientUsername, setupConversation]);
}
